import axios from "axios";
import { reactive } from "vue";
import { API_BASE_URL } from "./api-url.js";

const authHttp = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

class AuthService {
  constructor() {
    this.state = reactive({
      user: null,
      memberships: [],
      isAuthenticated: false,
      initialized: false,
    });
    this.initializationPromise = null;
    this.unauthorizedHandler = null;
  }

  setUnauthorizedHandler(handler) {
    this.unauthorizedHandler = handler;
  }

  setAuthenticatedUser(user, memberships) {
    this.state.user = user;
    this.state.isAuthenticated = Boolean(user);
    if (memberships) this.state.memberships = memberships;
  }

  clearSession() {
    this.state.user = null;
    this.state.memberships = [];
    this.state.isAuthenticated = false;
  }

  handleUnauthorized() {
    this.clearSession();
    if (this.unauthorizedHandler) {
      void this.unauthorizedHandler();
    }
  }

  getCurrentUser() {
    return this.state.user;
  }

  isAuthenticated() {
    return this.state.isAuthenticated;
  }

  async ensureInitialized() {
    if (this.state.initialized) {
      return this.state.isAuthenticated;
    }

    if (!this.initializationPromise) {
      this.initializationPromise = this.validateSession().finally(() => {
        this.state.initialized = true;
      });
    }

    return this.initializationPromise;
  }

  async login(credentials) {
    try {
      const { data } = await authHttp.post("/auth/login", credentials);
      if (!data.user) {
        throw new Error("Respuesta inválida del servidor");
      }

      this.setAuthenticatedUser(data.user, data.memberships);
      this.state.initialized = true;
      return { success: true, user: data.user, message: data.message };
    } catch (error) {
      this.clearSession();
      // status se propaga aparte del mensaje: 403 = cuenta bloqueada por
      // fuerza bruta (mensaje distinto al 401 genérico de credenciales
      // inválidas) — ver Login.vue, que lo usa para pintar un aviso
      // distinto sin tener que adivinar por el texto.
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        status: error.response?.status,
      };
    }
  }

  async requestPasswordReset(email) {
    try {
      const { data } = await authHttp.post("/auth/password-reset/request", {
        email,
      });
      return { success: true, message: data.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  }

  async confirmPasswordReset(token, newPassword) {
    try {
      const { data } = await authHttp.post("/auth/password-reset/confirm", {
        token,
        newPassword,
      });
      return { success: true, message: data.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  }

  // Público, sin sesión — lo usan ForgotEmail.vue (sin login) y el widget
  // de chat (autenticado, pero reusa el mismo endpoint público simple).
  async getSupportInfo() {
    try {
      const { data } = await authHttp.get("/public/support/info");
      return { success: true, phone: data.phone || "" };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  }

  // Cambia la organización activa de la sesión. Si `membershipId` está
  // bloqueada (no vino desbloqueada en el login), el backend exige `password`
  // y responde 401 si falta o es incorrecta — ver TopBar.vue.
  async switchOrganization(membershipId, password) {
    try {
      const { data } = await authHttp.post("/auth/switch-organization", {
        membershipId,
        ...(password ? { password } : {}),
      });
      this.setAuthenticatedUser(data.user, data.memberships);
      return { success: true, user: data.user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  }

  async createOrganization(payload) {
    try {
      const { data } = await authHttp.post("/auth/organizations", payload);
      this.setAuthenticatedUser(data.user, data.memberships);
      return { success: true, user: data.user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  }

  // No pide clave nueva: el backend desbloquea la membership nueva con la
  // misma clave que la membership activa de la sesión actual. Requiere
  // sesión — llamar solo cuando el usuario ya está autenticado.
  async acceptInvite(code) {
    try {
      const { data } = await authHttp.post("/auth/invites/accept", { code });
      this.setAuthenticatedUser(data.user, data.memberships);
      return { success: true, user: data.user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  }

  // No requiere sesión — validado por código+correo, igual que un link de
  // "cancelar suscripción".
  async declineInvite(code, email) {
    try {
      const { data } = await authHttp.post("/auth/invites/decline", {
        code,
        email,
      });
      return { success: true, message: data.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  }

  async register(userData) {
    try {
      const { data } = await authHttp.post("/auth/register", userData);
      return {
        success: true,
        user: data.user,
        message: data.message,
        requiresLogin: true,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  }

  async logout() {
    try {
      await authHttp.post("/auth/logout");
    } catch {
      // El logout local debe completarse aunque el backend no esté disponible.
    } finally {
      this.clearSession();
      this.state.initialized = true;
    }
  }

  async validateSession() {
    const wasAuthenticated = this.state.isAuthenticated;

    try {
      const { data } = await authHttp.get("/auth/profile");
      const user = data.user || data;
      this.setAuthenticatedUser(user, data.memberships);
      return true;
    } catch (error) {
      if (error.response?.status === 401 && wasAuthenticated) {
        this.handleUnauthorized();
      } else {
        this.clearSession();
      }
      return false;
    }
  }

  async refreshToken() {
    try {
      const { data } = await authHttp.post("/auth/refresh");
      const user = data.user || this.state.user;
      this.setAuthenticatedUser(user, data.memberships);
      return true;
    } catch (error) {
      if (error.response?.status === 401) {
        this.handleUnauthorized();
      }
      return false;
    }
  }

  async updateProfile(profile) {
    try {
      const { data } = await authHttp.patch("/auth/profile", profile);
      this.setAuthenticatedUser(data.user);
      return { success: true, user: data.user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  }

  async setRut(rut) {
    try {
      const { data } = await authHttp.patch("/auth/rut", { rut });
      this.setAuthenticatedUser(data.user);
      return { success: true, user: data.user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  }

  async changePassword(passwords) {
    try {
      const { data } = await authHttp.post("/auth/change-password", passwords);
      if (this.state.user) this.state.user.mustChangePassword = false;
      return { success: true, message: data.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  }
}

export const authService = new AuthService();
